import api from '../config/api';

class ParameterService {
    constructor() {
        this.cache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 dakika
        this.lastCacheUpdate = 0;
    }

    // Önbelleği temizle
    clearCache() {
        this.cache.clear();
        this.lastCacheUpdate = 0;
    }

    // Parametre değerini al (önbellekten)
    async getParameter(key, defaultValue = null) {
        // Önbellek süresi dolmuşsa güncelle
        if (Date.now() - this.lastCacheUpdate > this.cacheTimeout) {
            await this.refreshCache();
        }

        const param = this.cache.get(key);
        
        // Parametre yoksa veya pasifse null döndür
        if (!param || !param.isActive) {
            return null;
        }
        
        return param.value;
    }

    // Önbelleği güncelle
    async refreshCache() {
        try {
            const response = await api.get('/parameters');
            if (response.data.success) {
                this.cache.clear();
                response.data.data.forEach(param => {
                    this.cache.set(param.param_key, {
                        value: this.parseValue(param.param_value, param.param_type),
                        type: param.param_type,
                        isActive: param.is_active
                    });
                });
                this.lastCacheUpdate = Date.now();
            }
        } catch (error) {
            console.error('Parametre önbelleği güncellenirken hata:', error);
        }
    }

    // Değeri tipine göre parse et
    parseValue(value, type) {
        if (!value) return null;
        
        switch (type) {
            case 'number':
                return parseFloat(value);
            case 'boolean':
                return value.toLowerCase() === 'true';
            case 'json':
                try {
                    return JSON.parse(value);
                } catch {
                    return value;
                }
            default:
                return value;
        }
    }

    // Kendin tasarla ücretini al
    async getKendinTasarlaFee() {
        return await this.getParameter('kendin_tasarla_fee');
    }

    // Kargo ücreti hesapla
    async calculateShippingFee(cartTotal, paymentMethod = null) {
        const shippingEnabled = await this.getParameter('shipping_enabled');
        if (shippingEnabled === null || !shippingEnabled) {
            return null; // Kargo pasifse veya parametre yoksa null döndür
        }

        const freeShippingThreshold = await this.getParameter('free_shipping_threshold');
        const shippingFee = await this.getParameter('shipping_fee');
        const codShippingFee = await this.getParameter('cod_shipping_fee');

        // Gerekli parametreler yoksa null döndür
        if (freeShippingThreshold === null || shippingFee === null) {
            return null;
        }

        let baseShippingFee = 0;
        if (cartTotal < freeShippingThreshold) {
            baseShippingFee = shippingFee;
        }

        // Kapıda nakit ödeme için ekstra ücret
        if (paymentMethod === 'cod' && codShippingFee !== null) {
            baseShippingFee += codShippingFee;
        }

        return baseShippingFee;
    }

    // Kapıda nakit ödeme ücretini al
    async getCodShippingFee() {
        return await this.getParameter('cod_shipping_fee');
    }

    // Sistem durumunu kontrol et
    async isSystemMaintenance() {
        return await this.getParameter('maintenance_mode', false);
    }

    // Bakım mesajını al
    async getMaintenanceMessage() {
        return await this.getParameter('maintenance_message', 'Sistem bakımda, lütfen daha sonra tekrar deneyin.');
    }

    // SMS doğrulama aktif mi?
    async isSmsVerificationEnabled() {
        return await this.getParameter('sms_verification_enabled', true);
    }

    // Email doğrulama aktif mi?
    async isEmailVerificationEnabled() {
        return await this.getParameter('email_verification_enabled', true);
    }

    // Minimum sipariş tutarı
    async getMinOrderAmount() {
        return await this.getParameter('min_order_amount', 0);
    }

    // Maksimum sipariş tutarı
    async getMaxOrderAmount() {
        return await this.getParameter('max_order_amount', 10000);
    }

    // KDV oranı
    async getTaxRate() {
        return await this.getParameter('tax_rate', 18);
    }

    // İade süresi
    async getReturnDaysLimit() {
        return await this.getParameter('return_days_limit', 14);
    }

    // Sipariş zaman aşımı süresi
    async getOrderTimeoutMinutes() {
        return await this.getParameter('order_timeout_minutes', 30);
    }

    // Para birimi
    async getCurrency() {
        return await this.getParameter('currency', 'TRY');
    }

    // Maksimum dosya yükleme boyutu
    async getMaxUploadSizeMB() {
        return await this.getParameter('max_upload_size_mb', 10);
    }

    // İzin verilen dosya türleri
    async getAllowedFileTypes() {
        const types = await this.getParameter('allowed_file_types', 'jpg,jpeg,png,pdf');
        return types.split(',').map(type => type.trim());
    }

    // Önbellek süresi
    async getCacheDurationMinutes() {
        return await this.getParameter('cache_duration_minutes', 60);
    }

    // Rate limit ayarları
    async getRateLimitSettings() {
        const requests = await this.getParameter('rate_limit_requests', 100);
        const window = await this.getParameter('rate_limit_window_minutes', 1);
        return { requests, window };
    }
}

// Singleton instance
const parameterService = new ParameterService();

export default parameterService;
